/* Dependencies */
import anime from 'animejs';

/// Models
import {
  CartSucessItem,
  CartElements,
  CartNotificationState,
} from './CartNotification.model';

/**
 * Cart Notification Handler
 * Handles cart notifications and actions.
 */
class CartNotification {
  elements: CartElements;
  state: CartNotificationState = {
    cartItemCount: 0,
    successActive: false,
    errorActive: false,
  };

  constructor() {
    this.defineElements();
    this.getInitialCartCount();
    this.bindEvents();
  }

  /**
   * Defines the cart notification elements.
   */
  defineElements() {
    // Container
    this.elements = {
      cartCountElement: document.querySelector('[data-cart-count]'),
      cartLiveRegionElement: document.querySelector('[data-cart-live-region]'),
      cartNotificationSuccessElement: document.querySelector(
        '[data-cart-notification-success]'
      ),
      cartNotificationErrorElement: document.querySelector(
        '[data-cart-notification-error]'
      ),
      cartProductNameElement: document.querySelector(
        '[data-cart-notification-product-name]'
      ),
      cartProductVariantElement: document.querySelector(
        '[data-cart-notification-product-variant]'
      ),
      cartProductQuantityElement: document.querySelector(
        '[data-cart-notification-product-quantity]'
      ),
      cartNotificationErrorMessageElement: document.querySelector(
        '[data-cart-notification-error-message]'
      ),
      cartNotificationCloseElement: Array.from(
        document.querySelectorAll('[data-cart-notification-close]')
      ),
    };
  }

  /**
   * Attaches a global event handler when element is shown.
   */
  attachGlobalEventHandler(): void {
    // Create handler instance
    window.addEventListener(
      'keydown',
      (event: KeyboardEvent): void => {
        if (event.key === 'Escape') {
          const { successActive, errorActive } = this.state;

          // Hide the error message
          if (errorActive) {
            this.hideCartError();
          }

          // Hide the success message
          if (successActive) {
            this.hideCartSuccess();
          }
        }
      },
      { once: true }
    );
  }

  /**
   * Binds events to the cart notification elements.
   */
  bindEvents() {
    // Dismiss of notification.
    const { cartNotificationCloseElement } = this.elements;

    // Handle close element.
    cartNotificationCloseElement.forEach((closeElement) => {
      closeElement.addEventListener('click', (event) => {
        event.preventDefault();
        const { successActive, errorActive } = this.state;

        // Hide the error message
        if (errorActive) {
          this.hideCartError();
        }

        // Hide the success message
        if (successActive) {
          this.hideCartSuccess();
        }
      });
    });
  }

  /**
   * Retrieves the initial cart item count.
   */
  getInitialCartCount(): void {
    const { cartCountElement } = this.elements;

    // Pull the number from the element.
    this.state = {
      ...this.state,
      cartItemCount: +cartCountElement.getAttribute('data-cart-count'),
    };
  }

  /**
   * Increments the cart count.
   * @param increment - The amount to increment by. Defaults to 1.
   */
  incrementCartCount(increment: number = 1): void {
    const { cartCountElement } = this.elements;

    // Update state
    this.state = {
      ...this.state,
      cartItemCount: this.state.cartItemCount + increment,
    };

    // Render and update the element.
    cartCountElement.classList.remove('tw-hidden');
    cartCountElement.setAttribute(
      'data-cart-count',
      this.state.cartItemCount.toString()
    );
    cartCountElement.textContent = `(${this.state.cartItemCount})`;
  }

  /**
   * Updates the aria live region.
   * @param message - the message to be rendered.
   */
  updateLiveRegion(message: string): void {
    const { cartLiveRegionElement } = this.elements;
    cartLiveRegionElement.textContent = '';
    // Timeout used so that the same message is re-read.
    setTimeout(() => {
      cartLiveRegionElement.textContent = message;
    });
  }

  /**
   * Shows the cart success message.
   * @param item - The product item to be rendered.
   */
  showCartSuccess(item: CartSucessItem): void {
    // Hide cart error if previously shown.
    if (this.state.errorActive) {
      this.hideCartError();
    }

    // Update the cart count.
    this.incrementCartCount(item.productQuantity);

    // Update aria live regeon
    this.updateLiveRegion(
      `${item.productTitle} added to cart. Total Quantity: ${this.state.cartItemCount}`
    );

    // Extract required elements
    const {
      cartNotificationSuccessElement,
      cartProductNameElement,
      cartProductVariantElement,
      cartProductQuantityElement,
    } = this.elements;

    // Update success message content
    cartProductNameElement.textContent = item.productTitle;
    cartProductQuantityElement.textContent = item.productQuantity.toString();

    // Show optional variant
    if (item.productVariant) {
      cartProductVariantElement.textContent = item.productVariant;
    } else {
      cartProductVariantElement.textContent = '';
      cartProductVariantElement.classList.add('tw-hidden');
    }

    // Render success pop up.
    cartNotificationSuccessElement.classList.remove('tw-hidden');
    cartNotificationSuccessElement.classList.add('tw-block');

    // Update state
    this.state = {
      ...this.state,
      successActive: true,
    };

    // Bind handler
    this.attachGlobalEventHandler();

    // Re-attach the 3rd party shipping bar
    // @ts-ignore
    if (window.shippingBarHelper) {
      // @ts-ignore
      window.shippingBarHelper.pollForShippingBar();
    }
  }

  /**
   * Dismisses the cart success notification.
   */
  hideCartSuccess(): void {
    const { cartNotificationSuccessElement } = this.elements;

    // Hide element
    cartNotificationSuccessElement.classList.add('tw-hidden');
    cartNotificationSuccessElement.classList.remove('tw-block');

    // Update state
    this.state = {
      ...this.state,
      successActive: false,
    };
  }

  /**
   * Show a cart error message.
   * @param message - The error message.
   */
  showCartError(message: string): void {
    // Hide success if present.
    if (this.state.successActive) {
      this.hideCartSuccess();
    }

    // Update aria live regeon
    this.updateLiveRegion(`An error has occured: ${message}`);

    // Pull required elements
    const {
      cartNotificationErrorElement,
      cartNotificationErrorMessageElement,
    } = this.elements;

    // Update error message
    cartNotificationErrorMessageElement.textContent = message;

    // Render error pop up.
    cartNotificationErrorElement.classList.remove('tw-hidden');
    cartNotificationErrorElement.classList.add('tw-block');

    // Update state
    this.state = {
      ...this.state,
      errorActive: true,
    };

    // Bind handler
    this.attachGlobalEventHandler();
  }

  /**
   * Dismisses the cart error notification.
   */
  hideCartError(): void {
    const { cartNotificationErrorElement } = this.elements;

    // Hide element
    cartNotificationErrorElement.classList.add('tw-hidden');
    cartNotificationErrorElement.classList.remove('tw-block');

    // Update state
    this.state = {
      ...this.state,
      errorActive: false,
    };
  }
}

// Export the instance for use in other components.
export const cartNotification = new CartNotification();
